import { Component, inject, Input, ChangeDetectionStrategy } from '@angular/core'
import { Call, CallProcessingStatus, CallsService, isDraftCall } from '../calls.service'
import { CommonModule, NgOptimizedImage } from '@angular/common'
import { MatIconButton } from '@angular/material/button'
import { MatChip } from '@angular/material/chips'
import { MatIcon } from '@angular/material/icon'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatProgressBar } from '@angular/material/progress-bar'
import { MatTooltip } from '@angular/material/tooltip'
import { RouterLink } from '@angular/router'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { DurationPipe } from '../duration.pipe'
import { MatDialog } from '@angular/material/dialog'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { formatCounterpartyNames } from '../utils'
import { ToastService } from '../toast.service'
import { UsersService } from '../users.service'

@Component({
  selector: 'app-previous-meeting-card',
  standalone: true,
  imports: [
    CommonModule,
    MatChip,
    NgOptimizedImage,
    RouterLink,
    MatIcon,
    MatIconButton,
    TranslatePipe,
    TranslateDirective,
    MatTooltip,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatProgressBar,
    DurationPipe,
  ],
  templateUrl: './previous-meeting-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './previous-meeting-card.component.scss',
})
export class PreviousMeetingCardComponent {
  @Input({ required: true })
  call!: Call

  @Input()
  activeMatch: string | null = null

  @Input({ required: true })
  isActive!: boolean

  @Input()
  useCompactMode: boolean = false

  callsService = inject(CallsService)
  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  deleteCallMutation = this.callsService.deleteCallMutation()
  toggleNotificationSettingMutation = this.callsService.toggleNotificationSetting()
  toastService = inject(ToastService)
  usersService = inject(UsersService)

  onMenuClick(event: Event) {
    event.preventDefault()
    event.stopPropagation()
  }

  async toggleNotificationSetting(event: Event) {
    event.preventDefault()
    event.stopPropagation()
    const notifyWhenPostProcessingCompletes = !this.call.notifyWhenPostProcessingCompletes
    await this.toggleNotificationSettingMutation.mutateAsync({
      callId: this.call.id,
      notifyWhenPostProcessingCompletes,
    })
    if (notifyWhenPostProcessingCompletes) {
      this.toastService.show('CALLS.NOTIFICATIONS.ENABLED_TOAST')
    } else {
      this.toastService.show('CALLS.NOTIFICATIONS.DISABLED_TOAST')
    }
  }

  delete(call: Call): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.DELETE.DIALOG_TITLE'),
        description: this.translateService.instant('CALLS.DELETE.DIALOG_BODY', { callName: call.name }),
        confirmLabel: this.translateService.instant('COMMON.YES'),
      },
    })
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.deleteCallMutation.mutateAsync(call.id)
      }
    })
  }

  get formattedCounterpartyNames(): string {
    return formatCounterpartyNames(this.call.counterpartyNames)
  }

  getMatchLeft(call: Call, startTimeSeconds: number): number {
    return (startTimeSeconds / call.duration!) * 100
  }

  get isSplitting(): boolean {
    return (
      this.call.totalNumberOfSplits > 0 &&
      (this.call.processingStatus === CallProcessingStatus.SPLITTING_CALL || this.call.processingStatus === CallProcessingStatus.PROCESSING_MEDIA)
    )
  }

  get isDraft(): boolean {
    return isDraftCall(this.call)
  }

  get isSuperuser(): boolean {
    return this.usersService.currentUser$.value?.role === 'SUPER'
  }
}
